import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import TiktokOpsListToolbar from '@/tiktok-platform/components/TiktokOpsListToolbar';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import CategoryCascaderSelect from '@/tiktok-platform/components/CategoryCascaderSelect';
import TiktokSwitch from '@/tiktok-platform/components/TiktokSwitch';
import TiktokOpsCopyHoverMenu from '@/tiktok-platform/components/TiktokOpsCopyHoverMenu';
import TiktokTemplateScopeTabs from '@/tiktok-platform/components/TiktokTemplateScopeTabs';
import TiktokStyleNoBatchFilter from '@/tiktok-platform/components/TiktokStyleNoBatchFilter';
import TiktokTemplateNameBatchFilter from '@/tiktok-platform/components/TiktokTemplateNameBatchFilter';
import TiktokWorkspaceSiteTabs, { TIKTOK_WORKSPACE_SITE_ALL } from '@/tiktok-platform/components/TiktokWorkspaceSiteTabs';
import AdminSheinOfficialBatchMoveSitesModal from '@/pages/admin/components/AdminSheinOfficialBatchMoveSitesModal';
import useMaterialTemplatePortableActions from '@/tiktok-platform/modules/templates/useMaterialTemplatePortableActions';
import { formatBeijingDateTime } from '@/tiktok-platform/utils/formatBeijingDateTime';
import { cellDisplayTitle } from '@/tiktok-platform/utils/cellDisplayTitle';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import { MATERIAL_TEMPLATE_TYPES } from '@/tiktok-platform/utils/tiktokMaterialTemplatePortable';
import { listEnabledSites } from '@/tiktok-platform/utils/tiktokSiteList';
import {
  loadShopSiteListCached,
  peekShopSiteList,
} from '@/tiktok-platform/utils/tiktokTemplateScopeCache';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { matchTemplateName, templateNameTokens } from '@/tiktok-platform/utils/templateNameQuery';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import {
  buildTiktokWorkspaceBatchOpsMenu,
  buildTemplateCopyMenuOptions,
  resolveCopySourceScope,
  resolveCopyTargetScope,
  resolveTemplateRowShopId,
  rowCanCopyScopedTemplate,
  rowCanDeleteScopedTemplate,
  rowCanEditScopedTemplate,
  rowCanPublishScopedTemplate,
  templateCopiedToast,
} from '@/tiktok-platform/constants/tiktokTemplateScope';
import { openTiktokPathInNewTab } from '@/tiktok-platform/constants/tiktokRoutes';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/tiktok-platform/utils/tiktokTemplateVersionGuard';
import useTiktokTemplateScope from '@/tiktok-platform/hooks/useTiktokTemplateScope';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { usePlatformShopPick, resolveWriteShopId } from '@/hooks/usePlatformShopPick';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function rowKey(row) {
  return String(row?.id ?? '');
}

/** 团队模板可能归属队友店铺，API 需用模板上的 shopId */
const resolveRowShopId = resolveTemplateRowShopId;

const COL_SPAN_FULL = 11;
const COL_SPAN_OFFICIAL = 7;

/** 模板库 · 产品模板（整份发品草稿快照，列表对齐属性库） */
export default function ProductTemplatePage() {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, pageShopScope, scopedShops = [] } = useTiktokPageShopScope();
  const {
    scopeTab,
    changeScopeTab,
    listShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    canAccessTeam,
    categoryOptions,
    categories,
    categoriesLoading,
    ensureCategoriesLoaded,
  } = useTiktokTemplateScope({ preferredShopId: pageShopScope });

  
  
  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: listShopId,
  });

  const ensureWriteShopId = useCallback(async ({
    title = t('productTemplate.pickShopTitle'),
    description = t('productTemplate.pickShopDesc'),
    confirmText = t('productTemplate.confirm'),
  } = {}) => resolveWriteShopId({
    listAllShops,
    pageShopScope,
    listShopId,
    pickShop,
    title,
    description,
    confirmText,
  }), [listAllShops, pageShopScope, listShopId, pickShop, t]);

const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();

  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [styleNoDraft, setStyleNoDraft] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [siteAbbr, setSiteAbbr] = useState(TIKTOK_WORKSPACE_SITE_ALL);
  const [catalogSites, setCatalogSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [copyingId, setCopyingId] = useState(null);
  const [moving, setMoving] = useState(false);
  const [batchMovingSites, setBatchMovingSites] = useState(false);
  const [moveSitesOpen, setMoveSitesOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const tableColSpan = isOfficialTab ? COL_SPAN_OFFICIAL : COL_SPAN_FULL;

  useEffect(() => {
    if (!canQuery || !listShopId) {
      setCatalogSites([]);
      return undefined;
    }
    let cancelled = false;
    const cached = peekShopSiteList(listShopId);
    if (cached) {
      setCatalogSites(listEnabledSites(cached?.siteList ?? cached));
      setSitesLoading(false);
    } else {
      setSitesLoading(true);
    }
    loadShopSiteListCached(listShopId, () => tiktokPlatformAPI.getShopSiteList(listShopId))
      .then((res) => {
        if (!cancelled) setCatalogSites(listEnabledSites(res?.siteList ?? res));
      })
      .catch(() => {
        if (!cancelled && !cached) setCatalogSites([]);
      })
      .finally(() => {
        if (!cancelled) setSitesLoading(false);
      });
    return () => { cancelled = true; };
  }, [canQuery, listShopId]);

  useEffect(() => {
    setSiteAbbr(TIKTOK_WORKSPACE_SITE_ALL);
  }, [scopeTab, listShopId]);

  const load = useCallback(async () => {
    if (!canQuery || !listShopId) {
      setItems([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return;
    }
    setLoading(true);
    try {
      const nameTokens = templateNameTokens(keyword);
      const baseParams = {
        scope: scopeTab,
        styleNo: styleNo || undefined,
        siteAbbr: siteAbbr || undefined,
        productTypeId: productTypeId || undefined,
        ...(listAllShops ? { allShops: true } : {}),
      };
      if (nameTokens.length > 1) {
        const all = await fetchAllPages(async (pageNo, size) => {
          const res = await tiktokPlatformAPI.listProductTemplates(listShopId, {
            ...baseParams,
            page: pageNo,
            pageSize: size,
          });
          return {
            items: Array.isArray(res?.items) ? res.items : [],
            total: Number(res?.total) || 0,
          };
        });
        const filtered = all.filter((row) => matchTemplateName(row.name, nameTokens));
        const start = (Math.max(1, page) - 1) * pageSize;
        setItems(filtered.slice(start, start + pageSize));
        setTotal(filtered.length);
      } else {
        const res = await tiktokPlatformAPI.listProductTemplates(listShopId, {
          ...baseParams,
          keyword: nameTokens[0] || undefined,
          page,
          pageSize,
        });
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total) || 0);
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      // 后端素材模板未就绪时避免进页即弹出「请求的资源不存在」
      const missing = Number(e?.status) === 404 || /不存在|not found/i.test(String(e?.message || ''));
      if (!missing) showToast(e?.message || t('productTemplate.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, siteAbbr, productTypeId, page, pageSize, queryNonce, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedKeys.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
  }, [items, selectedKeys]);

  const selectableKeys = useMemo(
    () => items
      .filter((row) => rowCanDeleteScopedTemplate(row) || rowCanCopyScopedTemplate(row))
      .map(rowKey)
      .filter(Boolean),
    [items],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCategoryChange = (id) => {
    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : '';
    setCategoryIdDraft(id ? String(id) : '');
    setProductTypeIdDraft(nextTypeId);
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setStyleNo(styleNoDraft.trim());
    setProductTypeId(productTypeIdDraft);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setStyleNoDraft('');
    setStyleNo('');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setProductTypeId('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resolveSelectedRows = useCallback(() => {
    const map = selectedRowsRef.current;
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  }, [selectedKeys]);

  const resolvePageRows = useCallback(() => items, [items]);

  const resolveFilteredRows = useCallback(async () => {
    if (!canQuery || !listShopId) return [];
    const nameTokens = templateNameTokens(keyword);
    const baseParams = {
      scope: scopeTab,
      styleNo: styleNo || undefined,
      siteAbbr: siteAbbr || undefined,
      productTypeId: productTypeId || undefined,
      ...(listAllShops ? { allShops: true } : {}),
    };
    if (nameTokens.length > 1) {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await tiktokPlatformAPI.listProductTemplates(listShopId, {
          ...baseParams,
          page: pageNo,
          pageSize: size,
        });
        return {
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total) || 0,
        };
      });
      return all.filter((row) => matchTemplateName(row.name, nameTokens));
    }
    return fetchAllPages(async (pageNo, size) => {
      const res = await tiktokPlatformAPI.listProductTemplates(listShopId, {
        ...baseParams,
        keyword: nameTokens[0] || undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, siteAbbr, productTypeId]);

  const {
    exporting,
    onExportCsvFiltered,
    onExportCsvPage,
    onExportCsvSelected,
  } = useMaterialTemplatePortableActions({
    type: MATERIAL_TEMPLATE_TYPES.products,
    shopId: listShopId,
    filePrefix: t('productTemplate.filePrefix'),
    resolvePageRows,
    resolveSelectedRows,
    resolveFilteredRows,
    buildCsvRows: (rows) => rows.map((row) => ({
      [t('productTemplate.colPriority')]: row.sortOrder ?? 0,
      [t('productTemplate.colStyleNo')]: row.styleNo || '',
      [t('productTemplate.colName')]: row.name || '',
      [t('productTemplate.colRemark')]: row.remark || '',
      [t('productTemplate.colCategory')]: row.categoryPathLabel || row.productTypeId || '',
      [t('productTemplate.colAutoRule')]: row.autoRuleEnabled ? t('productTemplate.on') : t('productTemplate.off'),
      [t('productTemplate.colCreatedAt')]: formatBeijingDateTime(row.createdAt),
      [t('productTemplate.colUpdatedAt')]: formatBeijingDateTime(row.updatedAt),
      [t('productTemplate.colCreatedBy')]: row.createdByName || '',
      [t('productTemplate.colUpdatedBy')]: row.updatedByName || '',
    })),
    getOne: (id) => {
      const row = selectedRowsRef.current.get(String(id))
        || items.find((item) => String(item.id) === String(id));
      const sid = resolveRowShopId(row, listShopId);
      return tiktokPlatformAPI.getProductTemplate(sid, id, { scope: scopeTab });
    },
    listByProductType: async (productTypeId, ctx) => {
      const sid = ctx?.shopId || listShopId;
      const res = await tiktokPlatformAPI.listProductTemplates(sid, {
        scope: scopeTab,
        productTypeId: productTypeId || undefined,
        page: 1,
        pageSize: 200,
      });
      return Array.isArray(res?.items) ? res.items : [];
    },
    createOne: (body, ctx) => tiktokPlatformAPI.createProductTemplate(ctx?.shopId || listShopId, {
      ...body,
      scope: scopeTab,
    }),
    onImported: async () => {
      setSelectedKeys(new Set());
      setPage(1);
      setQueryNonce((n) => n + 1);
    },
  });

  const onBatchDelete = async () => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdDelete'), 'error');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('returnList.batchDelete'),
      message: t('productTemplate.batchDeleteMsg', { count: ids.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await tiktokPlatformAPI.batchDeleteProductTemplates(listShopId, ids);
      const deleted = Number(res?.deleted);
      showToast(t('productTemplate.deletedCount', { count: Number.isFinite(deleted) ? deleted : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchCopy = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToCopy'), 'warning');
      return;
    }
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdCopy'), 'error');
      return;
    }
    const track = await startJob({
      kind: 'productTemplateBatchCopy',
      shopId: writeShopId,
      label: t('productTemplate.copyJob', { count: ids.length }),
      silent: true,
      payload: {
        ids,
        sourceScope: scopeTab,
        targetScope,
      },
    });
    if (!track?.id) return;
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, (result) => {
        unsub();
        const view = result?.view || {};
        const copied = Number(view.imported ?? view.copied) || 0;
        const skipped = Number(view.skipped) || 0;
        const skippedStyleNos = Array.isArray(view.skippedStyleNos) ? view.skippedStyleNos : [];
        if (result?.ok) {
          if (copied > 0 && skipped > 0) {
            const sample = skippedStyleNos.slice(0, 5).join('、');
            const more = skippedStyleNos.length > 5 ? '…' : '';
            showToast(
              view.message || t('productTemplate.copiedSkipped', { copied, skipped, sample: `${sample}${more}` }),
              'warning',
              5000,
            );
          } else if (copied > 0) {
            showToast(view.message || t('productTemplate.copiedCount', { count: copied }), 'success');
          } else {
            showToast(view.message || t('productTemplate.noneCopied'), 'warning', 5000);
          }
          setPage(1);
          setQueryNonce((n) => n + 1);
        } else if (!result?.cancelled) {
          showToast(view.error || view.message || result?.error?.message || t('productTemplate.batchCopyFailed'), 'error');
        }
        resolve();
      });
    });
  };

  const onBatchMove = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToMove'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdMove'), 'error');
      return;
    }
    setMoving(true);
    try {
      const res = await tiktokPlatformAPI.moveProductTemplates(listShopId, {
        ids,
        targetScope,
      });
      const moved = Number(res?.moved);
      showToast(t('productTemplate.movedCount', { count: Number.isFinite(moved) ? moved : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveFailed'), 'error');
    } finally {
      setMoving(false);
    }
  };

  const openBatchMoveSitesPicker = () => {
    if (!canQuery || isOfficialTab || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToMoveSites'), 'warning');
      return;
    }
    if (!resolveSelectedRows().length) {
      showToast(t('productTemplate.missingDataMove'), 'error');
      return;
    }
    setMoveSitesOpen(true);
  };

  const onBatchMoveSites = async (selectedSites) => {
    const sites = Array.isArray(selectedSites)
      ? selectedSites.map((s) => String(s || '').trim()).filter(Boolean)
      : [];
    if (!sites.length) {
      showToast(t('productTemplate.pickTargetSite'), 'warning');
      return;
    }
    if (isOfficialTab) {
      showToast(t('productTemplate.officialNoMove'), 'warning');
      return;
    }
    const rows = resolveSelectedRows().filter((row) => rowCanEditScopedTemplate(row));
    if (!rows.length) {
      showToast(t('productTemplate.noEditableMove'), 'error');
      return;
    }
    setBatchMovingSites(true);
    try {
      let updated = 0;
      for (const row of rows) {
        const body = { selectedSites: sites };
        const version = parseTemplateVersion(row.version);
        if (version != null) body.version = version;
        await tiktokPlatformAPI.updateProductTemplate(
          resolveRowShopId(row, listShopId),
          row.id,
          body,
        );
        updated += 1;
      }
      showToast(t('productTemplate.movedSites', { count: updated }), 'success');
      setMoveSitesOpen(false);
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveSitesFailed'), 'error');
    } finally {
      setBatchMovingSites(false);
    }
  };

  const onCreateTemplate = async () => {
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.createPickTitle'),
      description: t('productTemplate.createPickDesc'),
      confirmText: t('productTemplate.createHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    openTiktokPathInNewTab(
      `templates/product/new?scope=${encodeURIComponent(scopeTab)}&shopId=${encodeURIComponent(writeShopId)}`,
    );
  };

  const batchCopying = isBusy('productTemplateBatchCopy', listShopId);
  const batchBusy = deleting || exporting || batchCopying || moving || batchMovingSites;

  const onDeleteOne = async (row) => {
    if (!canQuery || !row?.id) return;
    const ok = await showTiktokConfirm({
      title: t('productTemplate.deleteTitle'),
      message: t('productTemplate.deleteOne', { name: row.name || row.id }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await tiktokPlatformAPI.deleteProductTemplate(resolveRowShopId(row, listShopId), row.id);
      showToast(t('productTemplate.deleted'), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onCopyOne = async (row, targetScope) => {
    if (!canQuery || !row?.id || copyingId) return;
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const resolvedTarget = resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab });
    setCopyingId(row.id);
    try {
      await tiktokPlatformAPI.copyProductTemplate(writeShopId, {
        sourceId: row.id,
        sourceScope: resolveCopySourceScope(row, { isOfficialTab, scopeTab }),
        targetScope: resolvedTarget,
        // 保留原名；目标 scope 重名时由后端追加「-副本」
        name: String(row.name || '').trim() || undefined,
      });
      showToast(
        isOfficialTab
          ? `${templateCopiedToast(resolvedTarget)}${t('productTemplate.imageConverted', { platform: 'TikTok' })}`
          : templateCopiedToast(resolvedTarget),
        'success',
      );
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onToggleAutoRule = async (row, enabled) => {
    if (!canQuery || !row?.id || !rowCanEditScopedTemplate(row)) return;
    const id = row.id;
    const key = String(id);
    const prevEnabled = row.autoRuleEnabled === true || row.autoRuleEnabled === 1;
    setTogglingIds((prev) => new Set(prev).add(key));
    setItems((prev) => prev.map((item) => (
      String(item.id) === key ? { ...item, autoRuleEnabled: enabled } : item
    )));
    try {
      const body = { autoRuleEnabled: enabled };
      const version = parseTemplateVersion(row.version);
      if (version != null) body.version = version;
      const res = await tiktokPlatformAPI.updateProductTemplate(resolveRowShopId(row, listShopId), id, body);
      const nextVersion = parseTemplateVersion(res?.item?.version);
      setItems((prev) => prev.map((item) => (
        String(item.id) === key
          ? {
            ...item,
            autoRuleEnabled: enabled,
            ...(nextVersion != null ? { version: nextVersion } : {}),
          }
          : item
      )));
      showToast(enabled ? t('productTemplate.autoOn') : t('productTemplate.autoOff'), 'success');
    } catch (e) {
      setItems((prev) => prev.map((item) => (
        String(item.id) === key ? { ...item, autoRuleEnabled: prevEnabled } : item
      )));
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'product' }]), 'warning', 3000);
        try {
          const res = await tiktokPlatformAPI.getProductTemplate(resolveRowShopId(row, listShopId), id, { scope: row.scope || scopeTab });
          const item = res?.item || res || {};
          const serverEnabled = item.autoRuleEnabled === true || item.autoRuleEnabled === 1;
          const serverVersion = parseTemplateVersion(item.version);
          setItems((prev) => prev.map((itemRow) => (
            String(itemRow.id) === key
              ? {
                ...itemRow,
                autoRuleEnabled: serverEnabled,
                ...(serverVersion != null ? { version: serverVersion } : {}),
              }
              : itemRow
          )));
        } catch {
          // 单行刷新失败时保持回滚状态
        }
      } else {
        showToast(e?.message || t('productTemplate.autoUpdateFailed'), 'error');
      }
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const emptyHint = !canQuery
    ? t('returnList.pickShop')
    : (loading
      ? t('returnList.loading')
      : (keyword || styleNo || siteAbbr || productTypeId
        ? t('productTemplate.noMatch')
        : (isOfficialTab ? t('productTemplate.emptyOfficial') : t('productTemplate.empty'))));

  return (
    <>
      <TiktokSyncProgressSlot kinds={['productTemplateBatchCopy']} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || batchBusy}
            batchCopying={batchCopying}
            batchMoving={moving || batchMovingSites}
            batchOpsMenu={buildTiktokWorkspaceBatchOpsMenu({
              scopeTab,
              isOfficialTab,
              canAccessTeam,
              selectedCount: selectedKeys.size,
              onCopy: onBatchCopy,
              onMove: onBatchMove,
              onDelete: onBatchDelete,
              deleteDisabled: !canQuery || batchBusy || isOfficialTab,
              ...(isOfficialTab ? {} : {
                sites: catalogSites,
                onMoveSites: onBatchMoveSites,
                onPickMultiSites: openBatchMoveSitesPicker,
                moveSitesDisabled: batchBusy,
              }),
            })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!canQuery || loading || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: onExportCsvFiltered,
              onExportPage: onExportCsvPage,
              onExportSelected: onExportCsvSelected,
            })}
            extraActions={isOfficialTab ? [] : [
              {
                key: 'create',
                label: t('productTemplate.create'),
                tone: 'primary',
                disabled: !canQuery || loading || batchBusy,
                onClick: onCreateTemplate,
              },
            ]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--category">
              <span className="tko-field__label">{t('productTemplate.colCategory')}</span>
              <CategoryCascaderSelect
                shopId={listShopId}
                categories={categories}
                value={categoryIdDraft}
                disabled={!canQuery || loading}
                placeholder={!canQuery ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                emptyText={
                  !canQuery
                    ? t('returnList.pickShop')
                    : (categoriesLoading ? t('productTemplate.categoriesLoading') : t('productTemplate.noCategories'))
                }
                onOpen={ensureCategoriesLoaded}
                onChange={onCategoryChange}
              />
            </div>
            <TiktokStyleNoBatchFilter
              value={styleNoDraft}
              disabled={!canQuery}
              onChange={setStyleNoDraft}
              onEnter={runQuery}
            />
            <TiktokTemplateNameBatchFilter
              value={keywordDraft}
              disabled={!canQuery}
              onChange={setKeywordDraft}
              onEnter={runQuery}
            />
          </div>
          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--ghost tko-btn--sm"
              disabled={!canQuery || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!canQuery || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(canQuery) && loading}
        loadingLabel={t('productTemplate.querying')}>
        <TiktokTemplateScopeTabs
          scopeTab={scopeTab}
          canAccessTeam={canAccessTeam}
          disabled={!canQuery || loading}
          ariaLabel={t('productTemplate.scopeAria')}
          onChange={(tabId) => {
            changeScopeTab(tabId, {
              onReset: () => {
                setPage(1);
                setSelectedKeys(new Set());
                selectedRowsRef.current.clear();
                setQueryNonce((n) => n + 1);
              },
            });
          }}
        />
        <TiktokWorkspaceSiteTabs
          siteAbbr={siteAbbr}
          sites={catalogSites}
          loading={sitesLoading}
          disabled={!canQuery || loading}
          onChange={(next) => {
            setSiteAbbr(next || TIKTOK_WORKSPACE_SITE_ALL);
            setPage(1);
            setSelectedKeys(new Set());
            selectedRowsRef.current.clear();
            setQueryNonce((n) => n + 1);
          }}
        />
        <div className="tko-drafts__table-wrap tko-certs-table-wrap">
          <table
            className={`tko-publish__table tko-drafts__table tko-certs-table tko-tpl-table tko-tpl-table--product${isOfficialTab ? ' tko-tpl-table--product-official' : ''}`}
          >
            <colgroup>
              <col className="tko-certs-col tko-certs-col--check" />
              <col className="tko-certs-col tko-certs-col--order" />
              <col className="tko-certs-col tko-certs-col--style-no" />
              <col className="tko-certs-col tko-certs-col--name" />
              <col className="tko-certs-col tko-certs-col--category" />
              <col className="tko-certs-col tko-certs-col--switch" />
              {!isOfficialTab ? (
                <>
                  <col className="tko-certs-col tko-certs-col--createdAt" />
                  <col className="tko-certs-col tko-certs-col--updatedAt" />
                  <col className="tko-certs-col tko-certs-col--user" />
                  <col className="tko-certs-col tko-certs-col--user" />
                </>
              ) : null}
              <col className="tko-certs-col tko-certs-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-certs-th tko-certs-th--check"
                  onClick={() => {
                    if (!canQuery || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!canQuery || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="tko-certs-th tko-certs-th--order">{t('productTemplate.colPriority')}</th>
                <th scope="col" className="tko-certs-th">{t('productTemplate.colStyleNo')}</th>
                <th scope="col" className="tko-certs-th">{t('productTemplate.colName')}</th>
                <th scope="col" className="tko-certs-th">{t('productTemplate.colCategory')}</th>
                <th scope="col" className="tko-certs-th tko-certs-th--switch">{t('productTemplate.colAutoRule')}</th>
                {!isOfficialTab ? (
                  <>
                    <th scope="col" className="tko-certs-th">{t('productTemplate.colCreatedAt')}</th>
                    <th scope="col" className="tko-certs-th">{t('productTemplate.colUpdatedAt')}</th>
                    <th scope="col" className="tko-certs-th">{t('productTemplate.colCreatedBy')}</th>
                    <th scope="col" className="tko-certs-th">{t('productTemplate.colUpdatedBy')}</th>
                  </>
                ) : null}
                <th scope="col" className="tko-certs-th tko-certs-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!canQuery || loading || items.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKey(row);
                const canEdit = rowCanEditScopedTemplate(row);
                const canDelete = rowCanDeleteScopedTemplate(row);
                const canPublish = rowCanPublishScopedTemplate(row);
                const canCopy = rowCanCopyScopedTemplate(row);
                const canSelect = canDelete || canCopy;
                const rowShopId = resolveRowShopId(row, listShopId);
                return (
                  <tr
                    key={key}
                    className={canSelect ? 'tko-row--clickable' : undefined}
                    onClick={(e) => {
                      if (!canSelect) return;
                      if (e.target.closest('button, a, input, .tko-copy-hover, .tiktok-draft-actions')) return;
                      toggleOne(key);
                    }}
                  >
                    <td className="tko-certs-td tko-certs-td--check">
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        disabled={!canSelect}
                        checked={selectedKeys.has(key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          if (!canSelect) return;
                          toggleOne(key);
                        }}
                      />
                    </td>
                    <td className="tko-certs-td tko-certs-td--order">{row.sortOrder ?? 0}</td>
                    <td className="tko-certs-td" title={cellDisplayTitle(row.styleNo)}>{row.styleNo || '—'}</td>
                    <td className="tko-certs-td" title={cellDisplayTitle(row.name, row.remark)}>
                      <strong>{row.name || '—'}</strong>
                      {row.remark ? <div className="tko-muted">{row.remark}</div> : null}
                    </td>
                    <td className="tko-certs-td" title={cellDisplayTitle(row.categoryPathLabel || row.productTypeId)}>
                      {row.categoryPathLabel || row.productTypeId || '—'}
                    </td>
                    <td className="tko-certs-td tko-certs-td--switch">
                      <TiktokSwitch
                        checked={!!row.autoRuleEnabled}
                        disabled={!canEdit || deleting || togglingIds.has(key)}
                        ariaLabel={t('productTemplate.autoRuleAria', { name: row.name || t('productTemplate.templateFallback') })}
                        onChange={(next) => onToggleAutoRule(row, next)}
                      />
                    </td>
                    {!isOfficialTab ? (
                      <>
                        <td className="tko-certs-td tko-certs-td--time" title={cellDisplayTitle(formatBeijingDateTime(row.createdAt))}>
                          {formatBeijingDateTime(row.createdAt)}
                        </td>
                        <td className="tko-certs-td tko-certs-td--time" title={cellDisplayTitle(formatBeijingDateTime(row.updatedAt))}>
                          {formatBeijingDateTime(row.updatedAt)}
                        </td>
                        <td className="tko-certs-td" title={cellDisplayTitle(row.createdByName)}>{row.createdByName || '—'}</td>
                        <td className="tko-certs-td" title={cellDisplayTitle(row.updatedByName)}>{row.updatedByName || '—'}</td>
                      </>
                    ) : null}
                    <td className="tko-certs-td tko-certs-td--ops">
                      <div className="tiktok-draft-actions">
                        {canPublish ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => openTiktokPathInNewTab(
                              `products/publish?productTemplateId=${encodeURIComponent(row.id)}${rowShopId ? `&shopId=${encodeURIComponent(rowShopId)}` : ''}`,
                            )}
                          >
                            {t('productTemplate.publish')}
                          </button>
                        ) : null}
                        {canEdit ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => openTiktokPathInNewTab(
                              `templates/product/edit/${encodeURIComponent(row.id)}?scope=${encodeURIComponent(row.scope || scopeTab)}${rowShopId ? `&shopId=${encodeURIComponent(rowShopId)}` : ''}`,
                            )}
                          >
                            {t('productTemplate.edit')}
                          </button>
                        ) : null}
                        {canCopy ? (
                          <TiktokOpsCopyHoverMenu
                            disabled={deleting || copyingId != null}
                            busy={copyingId === row.id}
                            options={buildTemplateCopyMenuOptions({
                              scopeTab,
                              isOfficialTab,
                              canAccessTeam,
                              onCopy: (target) => onCopyOne(row, target),
                            })}
                          />
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => onDeleteOne(row)}
                          >
                            {t('returnList.delete')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('productTemplate.pager')}
        />
      </ProductListCard>

      <AdminSheinOfficialBatchMoveSitesModal
        open={moveSitesOpen}
        selectedCount={selectedKeys.size}
        sites={catalogSites}
        busy={batchMovingSites}
        onClose={() => {
          if (!batchMovingSites) setMoveSitesOpen(false);
        }}
        onConfirm={onBatchMoveSites}
      />
      {shopPickModal}
    </>
  );
}
